// ===== AGB ANALYTICS MODULE =====
// Event tracking and transport layer
AGB.define('analytics', ['core', 'store'], function (Core, Store) {
    const { config, ErrorHandler } = Core;

    // Configuration
    const EXTERNAL_URL = 'https://analytics-ingest-432657747828.europe-west1.run.app/api/gear-builder/log';  // GCP-native ingest: Cloud Run -> Pub/Sub -> BigQuery events. (Was database-app-1 on Render.)
    const STORAGE_KEY = 'AGB_ANALYTICS';
    const SESSION_ID_KEY = 'AGB_SESSION_ID';
    const SESSION_START_KEY = 'AGB_SESSION_START';

    // Production mode - no test logs
    const TEST_LOGS = false;

    // NARROWED (2026-06-25, per Dorian): of all the gear-builder events this module
    // can emit, track ONLY check / uncheck / info — every other event is intentionally
    // dropped at saveEvent(). Reuses this stream's transport + the GCP analytics-ingest
    // route (/api/gear-builder/log -> Pub/Sub cae-events -> BigQuery events, source='gear_builder').
    const GB_ALLOWED_EVENTS = {
        add_suggestion: 1,        // CHECK   (DEBUG_LOGGER.addSuggestion)
        delete_suggestion: 1,     // UNCHECK (DEBUG_LOGGER.deleteSuggestion)
        suggestion_info_click: 1  // INFO    (DEBUG_LOGGER.helpClick)
    };

    // Session helpers
    function uid() {
        return (crypto?.randomUUID?.() ||
            (Date.now().toString(36) + Math.random().toString(36).slice(2, 10))).toUpperCase();
    }

    function getSessionId() {
        let sid = sessionStorage.getItem(SESSION_ID_KEY);
        if (!sid) {
            sid = uid();
            sessionStorage.setItem(SESSION_ID_KEY, sid);
        }
        return sid;
    }

    function getSessionStartTime() {
        let startTime = sessionStorage.getItem(SESSION_START_KEY);
        if (!startTime) {
            startTime = Date.now();
            sessionStorage.setItem(SESSION_START_KEY, startTime);
        }
        return parseInt(startTime, 10);
    }

    function getTimeFromStart() {
        return Math.round((Date.now() - getSessionStartTime()) / 1000);
    }

    function getCookie(name) {
        const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
        return m ? decodeURIComponent(m[1]) : null;
    }

    function detectBrowserDevice() {
        const ua = navigator.userAgent || '';
        const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua) || (window.innerWidth || 0) < 900;
        let browser = 'Unknown';
        if (/Edg\//.test(ua)) browser = 'Edge';
        else if (/OPR\//.test(ua)) browser = 'Opera';
        else if (/Chrome\//.test(ua)) browser = 'Chrome';
        else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';
        else if (/Firefox\//.test(ua)) browser = 'Firefox';
        else if (/MSIE|Trident/.test(ua)) browser = 'IE';
        return { browser, device: isMobile ? 'mobile' : 'desktop' };
    }

    // The suggestion's REAL position = the rendered card's data-suggestion-number,
    // stamped at render time from the FINAL list order — so it is correct whether
    // the suggestions came from the hardcoded maps or the control-panel
    // gear-suggestions envelope (the render order IS the merged truth for both).
    // Resolved by handle from the DOM because the emit can happen store-side with
    // no card element in scope. Returns null (not a fake 1) when the add didn't
    // come from a suggestion card (e.g. bundle-included items) — the old
    // Store.suggestionIndex counter was never incremented anywhere, which is why
    // every event logged suggestion_number: 1 (bug fixed 2026-07-13).
    function getSuggestionNumber(handle) {
        try {
            if (handle) {
                const li = document.querySelector(
                    '#suggestions-list li[data-handle="' + String(handle).replace(/"/g, '\\"') + '"]'
                );
                const n = li && +li.dataset.suggestionNumber;
                if (Number.isFinite(n) && n > 0) return n;
            }
        } catch { /* fall through */ }
        return null;
    }

    // Save event to localStorage
    // Dedup state for the double-emit below.
    let _lastSaved = { key: '', at: 0 };
    function saveEvent(eventData) {
        try {
            // console.log('saveEvent', eventData);
            if (!config.ANALYTICS_ENABLED) return;
            // Only the 3 interactions Dorian asked to track; every other gear-builder
            // event is intentionally NOT collected (see GB_ALLOWED_EVENTS above).
            if (!eventData || !GB_ALLOWED_EVENTS[eventData.event]) return;

            // De-double: one suggestion interaction fires the SAME event twice when
            // the shopper clicks the card BODY -- the card 'click' handler AND the
            // checkbox 'change' handler both emit (agb-suggest.js). Collapse an
            // identical event+handle+number within a short window. A real toggle
            // (add -> delete -> add) alternates the event name, so it is NEVER
            // collapsed; only the simultaneous duplicate is.
            const _p = eventData.parameters || {};
            const _key = eventData.event + '|' + (_p.handle || '') + '|' + (_p.suggestion_number != null ? _p.suggestion_number : '');
            const _now = Date.now();
            if (_lastSaved.key === _key && (_now - _lastSaved.at) < 700) return;
            _lastSaved.key = _key; _lastSaved.at = _now;

            const eid = uid();
            const state = Store.getState();
            const baseItem = state?.baseItem;

            const enriched = {
                ...eventData,
                eid,
                // base_handle: the loadout's BASE product handle, emitted on EVERY
                // gear-builder event so the analytics-ingest shaper can populate
                // BigQuery's product_handle column for base/session rows
                // (gear_builder_started / base_item_edit), not just ATC rows.
                // Distinct field (NOT reusing base_item_name.name, which is the title).
                base_handle: baseItem?.handle || null,
                base_item_name: {
                    name: baseItem?.title || baseItem?.handle || 'unknown',
                    handle: baseItem?.handle || null,
                    color: baseItem?.color || 'unknown',
                    size: baseItem?.size || 'unknown'
                }
            };

            const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
            existing.push(enriched);

            // colsole.log(enriched)

            const cap = Number(config.MAX_KEEP_EVENTS ?? 500);
            if (existing.length > cap) {
                existing.splice(0, existing.length - cap);
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));

            // Emit when the build-time TEST_LOGS flag is on OR the panel's
            // preview-only "gear_builder" console category is enabled
            // (window.AGB_consoleCat — preview-only, false on live).
            if (TEST_LOGS || (window.AGB_consoleCat && window.AGB_consoleCat('gear_builder'))) console.log(`[AGB Analytics] ${enriched.event}`, enriched.parameters, `(${existing.length} pending)`);

            // Trigger flush if threshold reached
            Transport.maybeFlush(existing.length);
        } catch (e) {
            // Handle localStorage quota exceeded
            if (e.name === 'QuotaExceededError') {
                try {
                    // Emergency flush to free space
                    Transport.flush({ force: true });
                    // Retry with just the new event
                    localStorage.setItem(STORAGE_KEY, JSON.stringify([enriched]));
                } catch (retryError) {
                    // Ultimate fallback: disable analytics for this session
                    config.ANALYTICS_ENABLED = false;
                }
            }
            // Other errors: silently fail in production
        }
    }

    // Public DEBUG_LOGGER API
    const DEBUG_LOGGER = {
        // Legacy log function
        log(event, data) {
            if (window.__AGB_DEBUG__) {
                console.log(`[AGB-LEGACY] ${event}:`, data);
            }
        },

        builderStarted(baseColor, baseSize) {
            saveEvent({
                event: 'gear_builder_started',
                time: getTimeFromStart(),
                parameters: {
                    base_product_color: baseColor || 'unknown',
                    base_product_size: baseSize || 'unknown'
                },
                timestamp: new Date().toISOString()
            });
        },

        baseItemEdit(baseColor, baseSize) {
            saveEvent({
                event: 'base_item_edit',
                time: getTimeFromStart(),
                parameters: {
                    base_product_color: baseColor || 'unknown',
                    base_product_size: baseSize || 'unknown'
                },
                timestamp: new Date().toISOString()
            });
        },

        addSuggestion(handle, nOverride) {
            saveEvent({
                event: 'add_suggestion',
                time: getTimeFromStart(),
                parameters: {
                    handle: handle || 'unknown',
                    suggestion_number: Number.isFinite(+nOverride) ? +nOverride : getSuggestionNumber(handle),
                },
                timestamp: new Date().toISOString()
            });
        },

        rejectSuggestion(handle, nOverride) {
            saveEvent({
                event: 'reject_suggestion',
                time: getTimeFromStart(),
                parameters: {
                    handle: handle || 'unknown',
                    suggestion_number: Number.isFinite(+nOverride) ? +nOverride : getSuggestionNumber(handle),
                },
                timestamp: new Date().toISOString()
            });
        },

        deleteSuggestion(handle, nOverride) {
            saveEvent({
                event: 'delete_suggestion',
                time: getTimeFromStart(),
                parameters: {
                    handle: handle || 'unknown',
                    suggestion_number: Number.isFinite(+nOverride) ? +nOverride : getSuggestionNumber(handle),
                },
                timestamp: new Date().toISOString()
            });
        },

        addToCart(handle, items) {
            saveEvent({
                event: 'add_to_cart',
                time: getTimeFromStart(),
                parameters: {
                    handle: handle || 'unknown',
                    items: items || []
                },
                timestamp: new Date().toISOString()
            });
        },

        stickyAddToCart(handle, items) {
            saveEvent({
                event: 'sticky_add_to_cart',
                time: getTimeFromStart(),
                parameters: {
                    handle: handle || 'unknown',
                    items: items || []
                },
                timestamp: new Date().toISOString()
            });
        },

        share(meta = {}) {
            saveEvent({
                event: 'share',
                time: getTimeFromStart(),
                parameters: { ...meta },
                timestamp: new Date().toISOString()
            });
        },

        download() {
            saveEvent({
                event: 'download',
                time: getTimeFromStart(),
                parameters: {},
                timestamp: new Date().toISOString()
            });
        },

        checkout(items) {
            saveEvent({
                event: 'checkout',
                time: getTimeFromStart(),
                parameters: {
                    items: items || []
                },
                timestamp: new Date().toISOString()
            });
        },

        editSuggestedItem(handle, change) {
            saveEvent({
                event: 'edit_suggested_item',
                time: getTimeFromStart(),
                parameters: {
                    handle: handle || 'unknown',
                    change: change || 'unknown'
                },
                timestamp: new Date().toISOString()
            });
        },

        helpClick(handle, nOverride) {
            saveEvent({
                event: 'suggestion_info_click',
                time: getTimeFromStart(),
                parameters: {
                    handle: handle || 'unknown',
                    suggestion_number: Number.isFinite(+nOverride) ? +nOverride : getSuggestionNumber(handle),
                },
                timestamp: new Date().toISOString()
            });
        },

        mobileStep2Open(meta = {}) {
            if(meta.via === "programmatic") return;
            saveEvent({
                event: 'mobile_step2_open',
                time: getTimeFromStart(),
                parameters: {
                    via: meta.via || 'unknown',
                },
                timestamp: new Date().toISOString()
            });
        },

        mobileStep2Close(meta = {}) {
            saveEvent({
                event: 'mobile_step2_close',
                time: getTimeFromStart(),
                parameters: {
                    via: meta.via || 'unknown',
                },
                timestamp: new Date().toISOString()
            });
        },

        clickSuggestionNavigation(input) {
            let direction = typeof input === 'string' ? input : (input?.direction || 'unknown');
            const handle = (typeof input === 'object' && input?.handle) || 'unknown';
            const status = (typeof input === 'object' && input?.status) || 'unknown';

            saveEvent({
                event: 'click_suggestion_navigation',
                time: getTimeFromStart(),
                parameters: {
                    direction,
                    handle,
                    suggestion_status: status
                },
                timestamp: new Date().toISOString()
            });
        }
    };

    // Transport layer
    const Transport = (() => {
        let flushing = false;
        let intervalId = null;
        let lastFlush = 0;
        const lifecycleCleanup = [];

        // A browser that closed mid-session keeps its unsent events in
        // localStorage for as long as the shopper keeps the browser profile.
        // They were being sent whenever that person came back — BigQuery shows
        // gear-builder rows arriving up to 348 days after they happened
        // (measured 2026-09-10), stamped with today's received_at, which quietly
        // dates a click to the wrong week. Anything older than a week is now
        // dropped as the queue is read, so it is never sent and the write-back
        // below clears it out of storage.
        //
        // An event with no `timestamp`, or one that doesn't parse, is KEPT: no
        // emitter ships without one today, and a missing field must behave
        // exactly as it did before rather than silently binning data.
        // NB: saveEvent() reads the queue directly for the append; the purge
        // lives here because this is the read every SEND path goes through.
        const MAX_EVENT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

        function isFresh(ev) {
            try {
                const t = ev && ev.timestamp ? Date.parse(ev.timestamp) : NaN;
                if (!Number.isFinite(t)) return true;   // unknown age -> unchanged behaviour
                return (Date.now() - t) <= MAX_EVENT_AGE_MS;
            } catch { return true; }
        }

        function readAll() {
            let all;
            try { all = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
            catch { return []; }
            if (!Array.isArray(all)) return [];
            const fresh = all.filter(isFresh);
            // Write only when something was actually dropped, so in the normal
            // case this stays a read and touches no storage.
            if (fresh.length !== all.length) writeAll(fresh);
            return fresh;
        }

        function writeAll(arr) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(arr || []));
            } catch (e) {
                // Handle quota exceeded - silently fail
                if (e.name === 'QuotaExceededError') {
                    // Clear some old events to free space
                    try {
                        const reduced = (arr || []).slice(-50); // Keep only last 50
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(reduced));
                    } catch (retryError) {
                        // Give up and clear all
                        localStorage.removeItem(STORAGE_KEY);
                    }
                }
            }
        }

        function popBatch(n) {
            const all = readAll();
            if (!all.length) return [];
            const batch = all.slice(0, n);
            writeAll(all.slice(n));
            return batch;
        }

        function buildPayload(events) {
            return {
                session_id: getSessionId(),
                session_started_at: new Date(getSessionStartTime()).toISOString(),
                site: location.host,
                path: location.pathname + location.search,
                referrer: document.referrer || null,
                tz: (Intl.DateTimeFormat().resolvedOptions().timeZone || null),
                ua: navigator.userAgent,
                visitorID: getCookie('visitorID'),
                device: detectBrowserDevice().device,
                screen: {
                    width: window.screen?.width || null,
                    height: window.screen?.height || null,
                    availWidth: window.screen?.availWidth || null,
                    availHeight: window.screen?.availHeight || null,
                    pixelRatio: window.devicePixelRatio || 1
                },
                events: events.slice(0, config.BATCH_SIZE)
            };
        }

        async function flush({ useBeacon = false, force = false } = {}) {
            // Fake Customer Engine: a synthetic visitor sends NO gear-builder
            // analytics (drop events entirely) — nothing leaves the browser.
            try { if (window.SE_isInternalSession && window.SE_isInternalSession()) return false; } catch (e) {}
            if (flushing && !force) return false;
            if (!config.ANALYTICS_ENABLED) return false;

            const events = popBatch(config.BATCH_SIZE);
            if (!events.length) return false;

            const payload = buildPayload(events);
            const body = JSON.stringify(payload);

            // Try beacon for unload
            if (useBeacon && navigator.sendBeacon) {
                try {
                    const ok = navigator.sendBeacon(EXTERNAL_URL, new Blob([body], { type: 'application/json' }));
                    if (ok) {
                        lastFlush = Date.now();
                        return true;
                    }
                } catch (err) {
                    // Silently fail in production
                }
                // Re-queue on beacon failure
                const cur = readAll();
                writeAll(events.concat(cur));
                return false;
            }

            // Normal fetch
            try {
                flushing = true;
                const res = await fetch(EXTERNAL_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                    credentials: 'omit',
                    mode: 'cors'
                });

                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                }

                lastFlush = Date.now();
                flushing = false;

                // Chain flush if more events queued
                if (readAll().length >= config.BATCH_SIZE) {
                    setTimeout(() => flush(), 100);
                }

                return true;
            } catch (err) {
                flushing = false;
                // Silently fail in production
                // Re-queue events
                const cur = readAll();
                writeAll(events.concat(cur));
                return false;
            }
        }

        function maybeFlush(len) {
            if (len >= config.FLUSH_ACTIVITY_THRESHOLD) {
                flush();
            }
        }

        function start() {
            if (intervalId) return;
            intervalId = setInterval(() => {
                if (Date.now() - lastFlush >= config.FLUSH_INTERVAL_MS) {
                    flush();
                }
            }, config.FLUSH_INTERVAL_MS);

            // Wire lifecycle events with cleanup tracking
            const pagehideHandler = () => flush({ useBeacon: true });
            const visibilityHandler = () => {
                if (document.visibilityState === 'hidden') flush({ useBeacon: true });
            };
            const beforeunloadHandler = () => flush({ useBeacon: true });
            const onlineHandler = () => flush();

            window.addEventListener('pagehide', pagehideHandler);
            document.addEventListener('visibilitychange', visibilityHandler);
            window.addEventListener('beforeunload', beforeunloadHandler);
            window.addEventListener('online', onlineHandler);

            // Store cleanup functions
            lifecycleCleanup.push(
                () => window.removeEventListener('pagehide', pagehideHandler),
                () => document.removeEventListener('visibilitychange', visibilityHandler),
                () => window.removeEventListener('beforeunload', beforeunloadHandler),
                () => window.removeEventListener('online', onlineHandler)
            );
        }

        function stop() {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }

            // Clean up event listeners
            lifecycleCleanup.forEach(fn => {
                try { fn(); } catch (e) { /* silently fail */ }
            });
            lifecycleCleanup.length = 0;

            // Flush any remaining events before stopping
            flush({ useBeacon: true });
        }

        function getStats() {
            return {
                pendingEvents: readAll().length,
                lastFlush: lastFlush ? new Date(lastFlush).toISOString() : 'never',
                isRunning: !!intervalId,
                flushing
            };
        }

        return { flush, maybeFlush, start, stop, getStats };
    })();

    // Start transport when module loads
    function start() {
        Transport.start();
    }

    // Expose on window for external access
    window.DEBUG_LOGGER = DEBUG_LOGGER;
    window.AGB = window.AGB || {};
    window.AGB.Transport = Transport;

    return {
        start,
        stop: Transport.stop,
        DEBUG_LOGGER,
        Transport,
        getSessionId,
        getTimeFromStart
    };
});